import { useState } from 'react';

const UNSEEN = Symbol('unseen');

/**
 * Reset (or otherwise adjust) this component's state when `key` changes, such as the selected visual's
 * identity. `reset` runs while rendering and React re-renders at once with the adjusted state, before the
 * screen updates: no effect, and no frame that shows the previous identity's state. `reset` may only set
 * this component's state.
 *
 * - `firstRender`: also run for the key the component mounts with.
 * - `when`: hold a changed key back until this is true (a gesture or save in progress); it counts as seen
 *   only once `reset` has run for it.
 *
 * `reset` receives the key it last ran for (undefined on the first run), for adjustments that compare the
 * old value with the new one.
 *
 * Prefer useKeyedState for a single value, or a `key` on a child component when everything in it starts over.
 */
export function useResetWhenChanged<K>(
    key: K,
    reset: (previous: K | undefined) => void,
    options: { firstRender?: boolean; when?: boolean } = {},
): void {
    const [seen, setSeen] = useState<K | typeof UNSEEN>(() => (options.firstRender ? UNSEEN : key));
    if (!Object.is(seen, key) && options.when !== false) {
        setSeen(key);
        reset(seen === UNSEEN ? undefined : seen);
    }
}
